import { Pressable, ScrollView } from "react-native";
import { Link, type Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { activationCameraKey, type ActivationSummary, type CursorPager } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";
import { ActivationStatus } from "./ActivationStatus";
import { CursorPagerBar, countOf } from "./CursorPagerBar";

interface ActivationsTableProps<T extends ActivationSummary> {
  readonly items: readonly T[];
  /** Dim the rows while they are the previous page's, held until the next arrives. */
  readonly isStale: boolean;
  readonly hrefFor: (activation: T) => Href;
  /** Adds a Tenant column, for a list spanning tenants. */
  readonly tenantLabel?: (activation: T) => string;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  readonly pager: CursorPager;
  readonly nextCursor: string | null;
  readonly isFetching: boolean;
}

const COLUMNS = [
  { label: "Ticket", style: "ticket" },
  { label: "Created", style: "created" },
  { label: "Status", style: "status" },
  { label: "Description", style: "description" },
  { label: "Tenant", style: "tenant" },
  { label: "Site", style: "site" },
  { label: "Camera", style: "camera" },
  { label: "Priority", style: "priority" },
] as const;

export function ActivationsTable<T extends ActivationSummary>({
  items,
  isStale,
  hrefFor,
  tenantLabel,
  siteLabel,
  cameraLabel,
  pager,
  nextCursor,
  isFetching,
}: ActivationsTableProps<T>) {
  const columns = tenantLabel === undefined ? COLUMNS.filter((c) => c.label !== "Tenant") : COLUMNS;

  return (
    <View flex={1}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.card, isStale && styles.stale]}>
          <View style={[styles.row, styles.headerRow]}>
            {columns.map((column) => (
              <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
                {column.label}
              </Text>
            ))}
          </View>
          {items.map((activation) => (
            <ActivationRow
              key={activation.ticketNo}
              activation={activation}
              href={hrefFor(activation)}
              {...(tenantLabel !== undefined && { tenantLabel: tenantLabel(activation) })}
              siteLabel={siteLabel}
              cameraLabel={cameraLabel}
            />
          ))}
          {items.length === 0 && (
            <View style={styles.row}>
              <Text textStyle="sm/regular" color="textMuted">
                {
                  nextCursor === null ?
                    "No more activations"
                    // The fleet list walks a bounded number of months per page,
                    // so an empty page can still have older activations behind it.
                  : "No activations in the months this page covered — older ones may follow"
                }
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      <CursorPagerBar
        pager={pager}
        nextCursor={nextCursor}
        isFetching={isFetching}
        summary={countOf(items.length, "activation")}
      />
    </View>
  );
}

interface ActivationRowProps {
  readonly activation: ActivationSummary;
  readonly href: Href;
  readonly tenantLabel?: string;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
}

function ActivationRow({ activation, href, tenantLabel, siteLabel, cameraLabel }: ActivationRowProps) {
  // `<Link asChild>` renders a real anchor, so cmd/middle-click opens the
  // activation in a new tab — the usual way to work through a list.
  return (
    <Link href={href} asChild>
      <Pressable accessibilityRole="link" style={styles.link}>
        {({ hovered }) => (
          <ActivationCells activation={activation} hovered={hovered} {...{ tenantLabel, siteLabel, cameraLabel }} />
        )}
      </Pressable>
    </Link>
  );
}

interface ActivationCellsProps extends Omit<ActivationRowProps, "href"> {
  readonly hovered?: boolean;
}

function ActivationCells({ activation, hovered = false, tenantLabel, siteLabel, cameraLabel }: ActivationCellsProps) {
  const cameraKey = activationCameraKey(activation);

  return (
    <View style={[styles.row, hovered && styles.hovered]}>
      <Text textStyle="sm/semibold" color="text" style={styles.ticket}>
        {activation.ticketNo}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.created}>
        {formatInstant(activation.createdDateTime)}
      </Text>
      <View style={styles.status}>
        <ActivationStatus activation={activation} />
      </View>
      <Text textStyle="sm/regular" color="text" style={styles.description} numberOfLines={2}>
        {formatText(activation.description)}
      </Text>
      {tenantLabel !== undefined && (
        <Text textStyle="xs/regular" color="text" style={styles.tenant} numberOfLines={1}>
          {tenantLabel}
        </Text>
      )}
      <Text textStyle="xs/regular" color="text" style={styles.site} numberOfLines={1}>
        {activation.a3sSite !== undefined ? siteLabel(activation.a3sSite) : EMPTY}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.camera} numberOfLines={1}>
        {cameraKey !== undefined ? cameraLabel(cameraKey) : EMPTY}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.priority}>
        {activation.priority !== undefined ? String(activation.priority) : EMPTY}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scroll: { flex: 1 },
  scrollContent: { padding: theme.spacing.xl },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  stale: { opacity: 0.5 },
  link: { cursor: "pointer" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  hovered: { backgroundColor: theme.colors.surfaceCardInner },
  ticket: { width: 90 },
  created: { width: 180 },
  status: { width: 150 },
  description: { flex: 2, minWidth: 140 },
  tenant: { flex: 1, minWidth: 120 },
  site: { flex: 1, minWidth: 100 },
  camera: { flex: 1, minWidth: 100 },
  priority: { width: 60, textAlign: "right" },
}));
